import React from 'react';
import {
  Box,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import { Notification } from '../../types/workflow';
import { getNotificationIcon } from './notificationUtils';

interface NotificationItemProps {
  notification: Notification;
  onClick?: (notification: Notification) => void;
}

/**
 * NotificationItem Component
 *
 * Displays a single notification with icon, title, message, and timestamp.
 * Shows unread indicator with subtle background color.
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const theme = useTheme();

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  const formattedTime = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  return (
    <ListItemButton
      onClick={handleClick}
      sx={{
        py: 1.5,
        px: 2,
        backgroundColor: notification.isRead
          ? 'transparent'
          : alpha(theme.palette.primary.main, 0.08),
        borderLeft: notification.isRead ? 'none' : '3px solid',
        borderLeftColor: notification.isRead ? 'transparent' : 'primary.main',
        '&:hover': {
          backgroundColor: notification.isRead
            ? 'action.hover'
            : alpha(theme.palette.primary.main, 0.12),
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 40,
          color: notification.isRead ? 'text.secondary' : 'primary.main',
        }}
      >
        {getNotificationIcon(notification.type)}
      </ListItemIcon>
      <ListItemText
        // Secondary content contains Box (<div>) elements; the default secondary
        // Typography renders a <p>, which would produce invalid DOM nesting.
        slotProps={{ secondary: { component: 'div' } }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.isRead ? 400 : 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {notification.title}
            </Typography>
            {notification.groupedCount > 1 && (
              <Chip
                label={`${notification.groupedCount} workflows`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
            {!notification.isRead && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {notification.message}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                }}
              >
                {formattedTime}
              </Typography>
              {notification.updatedAt && notification.updatedAt !== notification.createdAt && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'info.main',
                    fontSize: '0.65rem',
                    fontStyle: 'italic',
                  }}
                >
                  Updated
                </Typography>
              )}
            </Box>
          </Box>
        }
      />
    </ListItemButton>
  );
};

export default NotificationItem;
